import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  TextareaAutosize,
} from '@mui/material';
import {
  Copy,
  DownloadSimple,
  Pencil,
  PencilSlash,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../../service/ApiService';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import Worker from '../../../util/worker/textSegmentationWorker?worker';
import { WordCount } from '../../nodes/AiTextGenerator';
import OutputMarkDown from '../../OutputMarkdown/OutputMarkDown';
import KitModal from '../KitModal';
import LoadingSpinner from '../LoadingSpinner';

const segmentTypeCheck = (
  segmentType: string,
  segmentContent: string,
  autoPlay = false,
): Segment => {
  if (segmentType === 'link') {
    const imageRegex = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i;
    const audioRegex = /\.(mp3|wav|ogg|flac|aac|m4a)$/i;

    if (imageRegex.test(segmentContent)) {
      return {
        type: 'img',
        content: `<img src="${segmentContent}" alt="Image" />`,
      };
    } else if (audioRegex.test(segmentContent)) {
      return {
        type: 'audio',
        content: `<audio controls ${
          autoPlay && 'autoplay'
        }><source src="${segmentContent}" type="audio/mpeg"></audio>`,
      };
    } else {
      return {
        type: 'link',
        content: segmentContent,
      };
    }
  }

  return {
    type: segmentType as Segment['type'],
    content: segmentContent,
  };
};

export interface Segment {
  type:
    | 'code'
    | 'text'
    | 'table'
    | 'chart'
    | 'img'
    | 'audio'
    | 'link'
    | 'mermaid';
  content: string;
  isHtml?: boolean;
  title?: string;
}

export const TextComponent = ({
  item,
  onChange,
  user,
  onCopy,
  onHide,
  contentType = 'all',
  size,
  showDownload = false,
}: any) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(item.text);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setValue((prev: string) => {
      if (prev !== item.text) {
        return item.text;
      }

      return prev;
    });
  }, [item.text]);

  useEffect(() => {
    const worker = new Worker();
    worker.onmessage = (e: MessageEvent<Segment[]>) => {
      const newSegments = e.data;

      setSegments(newSegments);

      setLoading(false);
    };
    worker.postMessage({ text: value, batchSize: 10000 });

    return () => {
      worker.terminate();
    };
  }, [value]);

  const getSegmentBasedOnContentType = () => {
    if (contentType === 'code') {
      const codedSegments = segments.filter(segment => segment.type === 'code');
      return codedSegments;
    }
    if (contentType === 'table') {
      const tableSegments = segments.filter(
        segment => segment.type === 'table',
      );
      return tableSegments;
    }
    return segments;
  };

  const segmentsBasedOnContentType = getSegmentBasedOnContentType();
  const setNotification = useNotificationStore(state => state.setNotification);

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFormat = async (
    fileType: 'docx' | 'pdf',
    text: string,
  ) => {
    setIsDownloading(true);
    setAnchorEl(null);

    try {
      const { data: response } = await axiosInstance.post('/flux/output-file', {
        text: text || '',
        fileType,
      });

      const urlContentBlob = new Blob(
        [new Uint8Array(response.data.file.data).buffer],
        {
          type: fileType,
        },
      );

      const url = URL.createObjectURL(urlContentBlob);
      const link = document.createElement('a');

      link.href = url;
      link.download = response.data.name || `download.${fileType}`;

      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error: any) {
      console.error('Erro no download:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Erro ao fazer download do arquivo',
      });
    }

    setIsDownloading(false);
  };

  const open = Boolean(anchorEl);

  if (loading) {
    return <LoadingSpinner />;
  }

  const handleCancelEdit = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(item.text);
    }
  };

  const handleSaveEdit = () => {
    const cleanedValue = value.trimEnd();
    item.text = cleanedValue;
    onChange &&
      onChange({
        target: { name: item.name, value: cleanedValue },
      });
    setTimeout(() => {
      setIsEditing(false);
    }, 0);
  };

  return (
    <Box
      p={'8px'}
      sx={{
        borderRadius: '8px',
        border: isEditing ? '1px dashed #3650DE' : '1px solid #EAECF0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',

        '&:hover': {
          background:
            user?.theme === 'dark'
              ? item.edit
                ? '#333'
                : '#222'
              : item.edit
                ? '#FFF'
                : '#F9FAFB',
          cursor: item.edit ? 'text' : 'default',
        },
      }}
    >
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {item.label && (
          <Typography
            color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
            fontSize={'16px'}
            fontWeight={500}
            lineHeight={'157%'}
          >
            {item.label}
          </Typography>
        )}
        <Box>
          <IconButton onClick={() => onCopy(item.text)}>
            <Copy color='#98A2B3' size={16} />
          </IconButton>

          {showDownload && (
            <Tooltip title='Download text' arrow placement='top'>
              <IconButton
                onClick={handleDownloadClick}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <CircularProgress size={16} sx={{ color: '#3650de' }} />
                ) : (
                  <DownloadSimple color='#98A2B3' size={16} />
                )}
              </IconButton>
            </Tooltip>
          )}

          {item.edit ? (
            <Tooltip title='Edit text' arrow placement='top'>
              <IconButton onClick={() => setIsEditing(true)}>
                <Pencil color='#98A2B3' size={16} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                item.label === 'Output'
                  ? 'Output text is not editable.'
                  : 'Connected text not editable'
              }
              arrow
              placement='top'
            >
              <IconButton>
                <PencilSlash color='#98A2B3' size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {!isEditing ? (
        <Box
          color={user?.theme === 'dark' ? '#F9FAFB' : '#475467'}
          flex={1}
          minHeight={0}
          fontSize={'16px'}
          fontWeight={400}
          lineHeight={'143%'}
          sx={{
            overflowWrap: 'break-word',
            overflow: 'hidden auto',
          }}
        >
          <>
            {segmentsBasedOnContentType?.map((segment: any, index: number) => (
              <Box
                key={index}
                sx={{
                  '& p': {
                    margin: '0px',
                    '&:not(:last-child)': { marginBottom: '23px' },
                  },
                  marginBottom: '30px',
                }}
              >
                <OutputMarkDown
                  segment={segmentTypeCheck(segment.type, segment.content)}
                  onCopy={onCopy}
                  user={user}
                  index={index}
                  value={value}
                  segments={segmentsBasedOnContentType}
                />
              </Box>
            ))}
          </>
        </Box>
      ) : (
        <Box
          sx={{ paddingTop: '13px', paddingLeft: '6px', paddingRight: '10px' }}
        >
          <TextareaAutosize
            value={value}
            onKeyDown={handleCancelEdit}
            onChange={e => setValue(e.target.value)}
            minRows={1}
            style={{
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              maxWidth: '100%',
              width: '100%',
              fontSize: '16px',
              lineHeight: '143%',
              fontWeight: 400,
            }}
          />
        </Box>
      )}

      <Box
        onClick={() => {
          if (item.edit) {
            setIsEditing(true);
          }
        }}
        sx={{ flexShrink: 0 }}
      >
        <Box display={'flex'} justifyContent={'flex-end'} mt={'8px'}>
          {isEditing && (
            <Box>
              <IconButton onClick={handleSaveEdit}>
                <Typography
                  color={'#3650DE'}
                  fontSize={'16px'}
                  fontWeight={500}
                  lineHeight={'150%'}
                >
                  Save
                </Typography>
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Download Popover */}
      {showDownload && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleDownloadClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Button
              variant='text'
              onClick={() => {
                handleDownloadFormat('docx', item.text);
              }}
              fullWidth
              size='small'
              sx={{ justifyContent: 'flex-start', fontSize: '12px' }}
            >
              Download as DOCX
            </Button>
            <Button
              variant='text'
              onClick={() => {
                handleDownloadFormat('pdf', item.text);
              }}
              fullWidth
              size='small'
              sx={{ justifyContent: 'flex-start', fontSize: '12px' }}
            >
              Download as PDF
            </Button>
          </Box>
        </Popover>
      )}
    </Box>
  );
};

const SmallMemoryTextComponent = ({ item, onCopy, user }: any) => {
  const [processedSegments, setProcessedSegments] = useState<Segment[]>([]);

  const [isLoading, setLoading] = useState(true);
  const value = item.content;

  useEffect(() => {
    const worker = new Worker();
    worker.onmessage = (e: MessageEvent<Segment[]>) => {
      setProcessedSegments(e.data);
      setLoading(false);
    };
    worker.postMessage({ text: value, batchSize: 10000 });

    return () => {
      worker.terminate();
    };
  }, [value]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      p={'12px'}
      sx={{
        borderRadius: '8px',
        border: '1px solid #EAECF0',
        '&:hover': {
          background:
            user?.theme === 'dark'
              ? item.edit
                ? '#333'
                : '#222'
              : item.edit
                ? '#FFF'
                : '#F9FAFB',
          cursor: item.edit ? 'text' : 'default',
        },
      }}
    >
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <Typography
            color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
            fontSize={'16px'}
            fontWeight={500}
            lineHeight={'157%'}
          >
            Time Stamp:
          </Typography>
          <Typography
            color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
            fontSize={'16px'}
            lineHeight={'157%'}
          >
            {item.timestamp}
          </Typography>
        </Box>

        <Box>
          <Tooltip title={'This text is not editable.'} arrow placement='top'>
            <IconButton>
              <PencilSlash color='#98A2B3' size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <Typography
          color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
          fontSize={'16px'}
          fontWeight={500}
          lineHeight={'157%'}
        >
          Role:
        </Typography>
        <Typography
          color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
          fontSize={'16px'}
          lineHeight={'157%'}
        >
          {item.role.charAt(0).toUpperCase() + item.role.slice(1)}
        </Typography>
      </Box>
      <>
        {processedSegments?.map((segment, index) => (
          <OutputMarkDown
            onCopy={onCopy}
            segment={segment}
            user={user}
            key={index}
            index={index}
            value={value}
            segments={processedSegments}
          />
        ))}
      </>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={() => onCopy(item.content)}>
          <Copy size={19} color='#000' />
        </IconButton>
      </Box>
    </Box>
  );
};

type PreviewModalProps = {
  onHide: () => void;
  show: boolean;
  data: any;
  onChange?: (e: any) => void;
  name?: string;
  isSmallMemory?: boolean;
  nodeLabel?: string;
  type?: string;
  processedSegments?: any[];
};

const PreviewModal = ({
  onHide,
  show,
  data,
  onChange,
  name,
  isSmallMemory,
  nodeLabel,
  type,
  processedSegments,
}: PreviewModalProps) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);

    setNotification({
      type: 'success',
      message: 'Text copied to clipboard',
    });
  };

  const { user } = useAuthStore(state => state);

  return (
    <KitModal
      maxWidth={'md'}
      sx={{
        '& .MuiPaper-root': {
          overflow: 'hidden',
          maxHeight: '700px',
          width:
            data?.value?.length > 600 ||
            (data?.data?.length > 2 &&
              data?.data?.map((item: any) => item?.text?.length > 400))
              ? '1000px'
              : '500px',
          borderRadius: '16px',
          pb: '16px',
          backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
          borderTop: 'none',
          borderBottom: 'none',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
        p: '0px',
      }}
      fullWidth
      showCloseButton={true}
      show={show}
      onHide={onHide}
      title={
        <>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography
              color={user?.theme === 'dark' ? '#F9FAFB' : '#101828'}
              fontSize='16px'
              fontWeight={600}
              lineHeight='150%'
              sx={{
                maxWidth: '350px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={isSmallMemory ? data?.[0]?.name : data?.labelName}
            >
              {isSmallMemory ? data?.[0]?.name : data?.labelName}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={'6px'} mb={'10px'}>
            <Typography
              color={'#475467'}
              fontSize={'14px'}
              fontWeight={400}
              letterSpacing={'0.17px'}
              mt={0}
            >
              {data?.nodeLabel}
            </Typography>
            <Typography
              color={'#475467'}
              fontSize={'14px'}
              fontWeight={400}
              letterSpacing={'0.17px'}
            >
              {data?.value && data?.nodeLabel && '•'}
            </Typography>
            <WordCount
              text={data?.value}
              props={{
                color: '#475467',
                fontSize: '14px',
              }}
            />
          </Box>
        </>
      }
      headerStyles={{
        pb: '0px',
      }}
    >
      <Box maxHeight={550}>
        {isSmallMemory ? (
          data?.[0]?.entries ? (
            <Box
              display={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              {data?.[0]?.entries.map((item: any, index: number) => {
                return (
                  <SmallMemoryTextComponent
                    key={index}
                    item={item}
                    onCopy={onCopy}
                  />
                );
              })}
            </Box>
          ) : (
            <Box
              display={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              {data.map((item: any, index: number) => {
                return (
                  <SmallMemoryTextComponent
                    key={index}
                    item={item}
                    onCopy={onCopy}
                  />
                );
              })}
            </Box>
          )
        ) : data?.data?.length > 0 ? (
          <Box
            display={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {data?.data?.map((item: any, index: number) => {
              return (
                <TextComponent
                  key={index}
                  item={{ ...item, name }}
                  onChange={onChange}
                  onCopy={onCopy}
                  user={user}
                  onHide={onHide}
                  nodeLabel={nodeLabel}
                  processedSegments={processedSegments}
                  type={type}
                />
              );
            })}
          </Box>
        ) : (
          <TextComponent
            item={{
              text: data?.value,
              label: data?.labelName,
              edit: data?.readOnly ? false : true,
              name: name,
            }}
            onChange={onChange}
            onCopy={onCopy}
            user={user}
            onHide={onHide}
            processedSegments={processedSegments}
            type={type}
          />
        )}
      </Box>
    </KitModal>
  );
};

export default PreviewModal;
